import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import { WeeklySessionLimitModal } from '../../components/_molecules/BlockedModals';
import { WeeklySessionLimitState } from '../../components/_molecules/BlockedModals/WeeklySessionLimitModal';
import useAccessBadge, {
  LicenseCoarseLevels,
  UserAccessBadge,
} from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import { useMetrics } from '../../hooks/useMetrics';
import { usePaywallEmailTracking } from '../../hooks/usePaywallEmailTracking';
import useTeachingCreditState from '../../hooks/useTeachingCreditState';
import { TeachingCreditState } from '../../services/backendService/backendService';
import { isLessonAlreadyPlayedThisWeek as isLessonAlreadyPlayedThisWeekSelector } from './paywallSelectors';

interface PaywallContextType {
  promptPaywall: (source: string, message?: React.ReactNode) => void;
  showCreditInfo: (source: string) => void;
  hidePaywall: () => void;
  isShowingPaywall: boolean;
  shouldPromptPaywall: () => boolean;
  // Lesson-aware variant: returns false when the lesson is already in
  // `playedThisWeek`, mirroring the backend dedup key (presentationId +
  // slideCount) so replays don't get blocked at remaining=0.
  shouldPromptPaywallForLesson: (presentationId: string, slideCount: number) => boolean;
  isLessonAlreadyPlayedThisWeek: (presentationId: string, slideCount: number) => boolean;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
  hasReachedLessonLimit: boolean | undefined;
  teachingCreditState: TeachingCreditState | undefined;
  remaining: number | undefined;
  isLessonLimitEnabled: boolean;
  isLessonLimitLoading: boolean;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const PaywallProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { accessBadge: userAccessBadge, isFetched } = useAccessBadge();
  const {
    creditState: teachingCreditState,
    hasReachedLimit: hasReachedLessonLimit,
    remaining,
    isEnabled: isLessonLimitEnabled,
    isLoading: isLessonLimitLoading,
  } = useTeachingCreditState();
  const [isShowingPaywall, setIsShowingPaywall] = useState(false);
  // `hard-block` when a freemium teacher tries to start past the weekly limit;
  // `home` when they click the sidebar counter (info entry point). Default to
  // `home` so existing call sites that re-open via `showCreditInfo` aren't
  // surprised by a stale `hard-block` choice.
  const [paywallState, setPaywallState] = useState<WeeklySessionLimitState>('home');
  // Source captured at prompt time so it can be threaded into the modal and
  // attached to every CTA-click event fired from inside the modal.
  const [paywallSource, setPaywallSource] = useState<string | undefined>(undefined);
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const trackAttioEvent = useAttioEventTracking();
  const trackPaywallEmail = usePaywallEmailTracking();

  const shouldPromptPaywall = useCallback(() => {
    if (!isFetched || !userAccessBadge) return false;
    if (!isLessonLimitEnabled) return false;
    return (
      !!hasReachedLessonLimit &&
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    );
  }, [isFetched, userAccessBadge, isLessonLimitEnabled, hasReachedLessonLimit]);

  const isLessonAlreadyPlayedThisWeek = useCallback(
    (presentationId: string, slideCount: number) =>
      isLessonAlreadyPlayedThisWeekSelector(
        teachingCreditState,
        presentationId,
        slideCount,
      ),
    [teachingCreditState],
  );

  const shouldPromptPaywallForLesson = useCallback(
    (presentationId: string, slideCount: number) =>
      shouldPromptPaywall() &&
      !isLessonAlreadyPlayedThisWeek(presentationId, slideCount),
    [shouldPromptPaywall, isLessonAlreadyPlayedThisWeek],
  );

  const promptPaywall = useCallback(
    (source: string, _message?: React.ReactNode) => {
      if (shouldPromptPaywall()) {
        const eventProps = {
          source,
          used: teachingCreditState?.used,
          remaining,
          weeklyRate: teachingCreditState?.weeklyRate,
          bonusRemaining: teachingCreditState?.bonusRemaining,
          plan: userAccessBadge?.license?.coarseLevel,
          subPlan: userAccessBadge?.license?.fineLevel,
          organizationId: userAccessBadge?.organization?.id,
        };

        metrics.logEvent('TeachingSessions.StartBlocked', eventProps);
        trackEvent('teaching_sessions_start_blocked', eventProps);
        trackAttioEvent('paywall_blocked', {
          organizationId: userAccessBadge?.organization?.id,
          userRole: userAccessBadge?.user?.profileInfo?.role,
          email: userAccessBadge?.user?.email,
          firstName: userAccessBadge?.user?.firstName,
          lastName: userAccessBadge?.user?.lastName,
          source,
        });

        setPaywallState('hard-block');
        setPaywallSource(source);
        setIsShowingPaywall(true);
      }
    },
    [
      shouldPromptPaywall,
      teachingCreditState,
      remaining,
      userAccessBadge,
      metrics,
      trackEvent,
      trackAttioEvent,
    ],
  );

  const showCreditInfo = useCallback(
    (source: string) => {
      if (!isLessonLimitEnabled) return;

      metrics.logEvent('CreditInfo.Opened', {
        source,
        hasReachedLessonLimit,
        remaining,
        plan: userAccessBadge?.license?.coarseLevel,
        organizationId: userAccessBadge?.organization?.id,
      });

      setPaywallState('home');
      setPaywallSource(source);
      setIsShowingPaywall(true);
    },
    [isLessonLimitEnabled, metrics, hasReachedLessonLimit, remaining, userAccessBadge],
  );

  const hidePaywall = useCallback(() => {
    metrics.logEvent('TeachingSessions.PaywallDismissed', {
      hasReachedLessonLimit,
      remaining,
      plan: userAccessBadge?.license?.coarseLevel,
      organizationId: userAccessBadge?.organization?.id,
    });
    setIsShowingPaywall(false);
  }, [metrics, hasReachedLessonLimit, remaining, userAccessBadge]);

  const handleEmailSubmit = useCallback(
    (email: string) => {
      trackPaywallEmail(email, {
        paywallType: 'teaching-sessions',
        source: paywallSource,
        modalState: paywallState,
      });
      trackAttioEvent('paywall_email_submitted', {
        organizationId: userAccessBadge?.organization?.id,
        userRole: userAccessBadge?.user?.profileInfo?.role,
        email,
        firstName: userAccessBadge?.user?.firstName,
        lastName: userAccessBadge?.user?.lastName,
        source: paywallSource,
      });
    },
    [trackPaywallEmail, userAccessBadge, trackAttioEvent, paywallSource, paywallState],
  );

  const contextValue = useMemo<PaywallContextType>(
    () => ({
      promptPaywall,
      showCreditInfo,
      hidePaywall,
      isShowingPaywall,
      shouldPromptPaywall,
      shouldPromptPaywallForLesson,
      isLessonAlreadyPlayedThisWeek,
      isReady: isFetched,
      userAccessBadge,
      hasReachedLessonLimit,
      teachingCreditState,
      remaining,
      isLessonLimitEnabled,
      isLessonLimitLoading,
    }),
    [
      promptPaywall,
      showCreditInfo,
      hidePaywall,
      isShowingPaywall,
      shouldPromptPaywall,
      shouldPromptPaywallForLesson,
      isLessonAlreadyPlayedThisWeek,
      isFetched,
      userAccessBadge,
      hasReachedLessonLimit,
      teachingCreditState,
      remaining,
      isLessonLimitEnabled,
      isLessonLimitLoading,
    ],
  );

  return (
    <PaywallContext.Provider value={contextValue}>
      {children}
      <WeeklySessionLimitModal
        visible={isShowingPaywall}
        onClose={hidePaywall}
        state={paywallState}
        country={userAccessBadge?.organization?.country}
        region={userAccessBadge?.organization?.region}
        remaining={remaining}
        weeklyRate={teachingCreditState?.weeklyRate}
        renewsAt={teachingCreditState?.renewsAt}
        source={paywallSource}
        onEmailSubmit={handleEmailSubmit}
      />
    </PaywallContext.Provider>
  );
};

export const usePaywall = (): PaywallContextType => {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};
